import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX } from 'lucide-react';
import { Slider } from './ui/slider';
import { Button } from './ui/button';
import api from '../services/api';

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const TrackInfo = ({ track }) => (
  <div className="flex-1 min-w-0">
    <h4 className="text-white font-semibold truncate" style={{ fontFamily: 'Playfair Display, serif' }}>
      {track.title}
    </h4>
    <p className="text-gray-400 text-sm truncate">{track.artist}</p>
  </div>
);

const PlaybackControls = ({ isPlaying, onTogglePlay }) => (
  <div className="flex items-center space-x-4">
    <Button
      variant="ghost"
      size="icon"
      className="text-gray-400 hover:text-[#d4af37] transition-colors"
    >
      <SkipBack className="w-5 h-5" />
    </Button>
    <Button
      onClick={onTogglePlay}
      className="bg-[#d4af37] hover:bg-[#c5a572] text-[#0a1128] rounded-full w-12 h-12 transition-all duration-300 transform hover:scale-110"
      data-testid="play-pause-btn"
    >
      {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
    </Button>
    <Button
      variant="ghost"
      size="icon"
      className="text-gray-400 hover:text-[#d4af37] transition-colors"
    >
      <SkipForward className="w-5 h-5" />
    </Button>
  </div>
);

const VolumeControl = ({ volume, isMuted, onToggleMute, onVolumeChange }) => {
  const volumeValue = useMemo(() => [isMuted ? 0 : volume], [isMuted, volume]);

  return (
    <div className="hidden md:flex items-center space-x-3 flex-1 justify-end">
      <Button
        variant="ghost"
        size="icon"
        onClick={onToggleMute}
        className="text-gray-400 hover:text-[#d4af37] transition-colors"
      >
        {isMuted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
      </Button>
      <Slider
        value={volumeValue}
        max={100}
        step={1}
        onValueChange={onVolumeChange}
        className="w-24"
      />
    </div>
  );
};

const ProgressBar = ({ currentTime, duration, onTimeChange }) => {
  const timeValue = useMemo(() => [currentTime], [currentTime]);

  return (
    <div className="flex items-center space-x-3">
      <span className="text-xs text-gray-400 w-12 text-right">
        {formatTime(currentTime)}
      </span>
      <Slider
        value={timeValue}
        max={duration}
        step={1}
        onValueChange={onTimeChange}
        className="flex-1"
      />
      <span className="text-xs text-gray-400 w-12">
        {formatTime(duration)}
      </span>
    </div>
  );
};

const AudioPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(70);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(null);

  const fetchTracks = useCallback(async () => {
    try {
      const response = await api.getTracks();
      if (response.data && response.data.length > 0) {
        setCurrentTrack(response.data[0]);
      }
    } catch (err) {
      console.error('Error fetching tracks:', err);
    }
  }, []);

  useEffect(() => {
    fetchTracks();
  }, [fetchTracks]);

  const togglePlay = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  const handleTimeChange = useCallback((value) => {
    setCurrentTime(value[0]);
  }, []);

  const handleVolumeChange = useCallback((value) => {
    setVolume(value[0]);
    setIsMuted(false);
  }, []);

  useEffect(() => {
    if (!isPlaying || !currentTrack) return;
    const interval = setInterval(() => {
      setCurrentTime((prev) => {
        if (prev >= currentTrack.duration) {
          setIsPlaying(false);
          return 0;
        }
        return prev + 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isPlaying, currentTrack]);

  if (!currentTrack) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-[#0a1128] via-[#1e2749] to-[#0a1128] border-t border-[#d4af37]/30 backdrop-blur-lg shadow-2xl">
      <div className="container mx-auto px-6 py-4">
        <div className="flex flex-col space-y-3">
          <ProgressBar
            currentTime={currentTime}
            duration={currentTrack.duration}
            onTimeChange={handleTimeChange}
          />
          <div className="flex items-center justify-between">
            <TrackInfo track={currentTrack} />
            <PlaybackControls isPlaying={isPlaying} onTogglePlay={togglePlay} />
            <VolumeControl
              volume={volume}
              isMuted={isMuted}
              onToggleMute={toggleMute}
              onVolumeChange={handleVolumeChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
